import { 
  useEffect, 
  useMemo, 
  useRef, 
  useState 
} from "react";

import { 
  ListItem, 
  ProductHierarchyFilter, 
  FilterDropdown,
  SortSelectNew
} from "../../../../components/SharedComponents/sharedComponents.index";
import { SYSTEM_COLORS } from "../../../../components/system.colors";

import { i18n } from "../../../../translate/i18n";

import { OpportunityMetricsPanel } from './OpportunityMetrics/OpportunityMetricsPanel'

const { t } = i18n;

const TYPE_LABELS = {
  additionalSale: t("additionalSale"),
  avoidLoss: t("avoidSalesLoss"),
  stock: t("optimizePurchase"),
  optimizeBuy: t("buyOptimization"),
  trend: "Reversão de Tendência"
};

const TREND_OPTIONS = [
  { key: "up", label: "Alta" },
  { key: "down", label: "Queda" },
];

export const RecommendedActionsTable = ({ ctx, actions, uiState }) => {

  const {
    onClearFilters,
    handleRemoveOpportunity,
    resetModalState,
    setSelectedId,
    setRemovalReason,
    resetOppError
  } = actions || {};

  const {
    focusProduct = null,
    activeOppsWithTypes = [],
    options,
    removalReason,
    selectedId,
    products,
    opportunityTrendsDetails,
    detailsByLevel,
    hierarchy
  } = ctx || {};

  const { isLoadingTrendsDetails, updateOppError } = uiState || {}

  const [typeFilter, setTypeFilter] = useState([]);
  const [trendFilter, setTrendFilter] = useState([]);
  const [sort, setSort] = useState("value");

  const rowRefs = useRef({});

  const [highlightedId, setHighlightedId] = useState(null);
  const [hierarchyFilter, setHierarchyFilter] = useState([]);

  const productNameById = useMemo(() => {
    const levels = hierarchy?.levels ?? [];
    const lastLevelKey = levels[levels.length - 1];
    const entries = detailsByLevel?.[lastLevelKey] ?? [];

    const map = new Map();
    entries.forEach((entry) => {
      (entry.productIds ?? []).forEach((id) => map.set(id, entry.name));
    });
    return map;
  }, [detailsByLevel, hierarchy]);    

  const selectedProductIds = useMemo(() => {
    if (!hierarchyFilter.length) return null;

    const levels = hierarchy?.levels ?? [];

    const getProductIdsForPath = (path) => {
      const levelKey = levels[path.length - 1];
      const entries = detailsByLevel?.[levelKey] ?? [];
      const entry = entries.find((e) => e.path.join("\u0000") === path.join("\u0000"));
      return new Set(entry?.productIds ?? []);
    };

    const included = hierarchyFilter.filter((e) => !e.excluded);
    const excluded = hierarchyFilter.filter((e) => e.excluded);

    if (!included.length) return null;

    const isDescendantOf = (childPath, ancestorPath) =>
      ancestorPath.length < childPath.length && ancestorPath.every((seg, i) => childPath[i] === seg);

    const roots = included.filter((entry) =>
      !included.some((other) => other !== entry && isDescendantOf(entry.path, other.path))
    );

    const result = new Set();

    roots.forEach((root) => {
      const excludedIds = new Set();
      excluded
        .filter((ex) => isDescendantOf(ex.path, root.path) || ex.path.join("\u0000") === root.path.join("\u0000"))
        .forEach((ex) => getProductIdsForPath(ex.path).forEach((id) => excludedIds.add(id)));

      getProductIdsForPath(root.path).forEach((id) => {
        if (!excludedIds.has(id)) result.add(id);
      });
    });

    return result;
  }, [hierarchyFilter, detailsByLevel, hierarchy]);

  const typeOptions = useMemo(() => {
    const uniqueTypes = Array.from(new Set(activeOppsWithTypes.map((r) => r.type1).filter(Boolean)));
    return uniqueTypes.map((key) => ({ key, label: TYPE_LABELS[key] ?? key }));
  }, [activeOppsWithTypes]);

  const filtered = useMemo(() => {
    let rows = activeOppsWithTypes;

    if (typeFilter.length) rows = rows.filter((r) => typeFilter.includes(r.type1));
    if (trendFilter.length) rows = rows.filter((r) => trendFilter.includes(r.percent > 0 ? "up" : "down"));
    if (selectedProductIds) rows = rows.filter((r) => selectedProductIds.has(r.productId));

    const sorted = [...rows];

    if (sort === "name_asc") {
      sorted.sort((a, b) => {
        const nameA = productNameById.get(a.productId) ?? "";
        const nameB = productNameById.get(b.productId) ?? "";
        return nameA.localeCompare(nameB, "pt-BR");
      });
    } else {
      sorted.sort((a, b) => b.value - a.value);
    }

    return sorted;
  }, [activeOppsWithTypes, typeFilter, trendFilter, selectedProductIds, sort, productNameById]);

  useEffect(() => {
    if (!focusProduct?.productId) return;
    const target = filtered.find((r) => r.productId === focusProduct.productId);
    if (!target) return;
    rowRefs.current[target.opportunityId]?.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightedId(target.opportunityId);
    const timer = setTimeout(() => setHighlightedId(null), 2200);
    return () => clearTimeout(timer);
  }, [focusProduct, filtered]);

  const clearFilters = () => {
    setTypeFilter([]);
    setTrendFilter([]);
    setHierarchyFilter([]);
    onClearFilters?.();
  };

  return (
    <section style={{
      backgroundColor: SYSTEM_COLORS.WHITE.CLOUD, border: `1px solid ${SYSTEM_COLORS.GRAY.LIGHT_GRAY}`, borderRadius: "12px", 
      boxShadow: `0 1px 3px ${SYSTEM_COLORS.BLACK.BLACK_4_PERCENT}`, marginBlockEnd: "8rem",
      padding: "20px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
        <hgroup style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 4, height: 28, borderRadius: 2, background: SYSTEM_COLORS.RED.CORAL_RED }} />
          <h5 style={{ fontSize: '1.25rem', fontWeight: 700, color: SYSTEM_COLORS.BLACK.EERIE_BLACK }}>Ações Recomendadas</h5>
          <p style={{ fontSize: 12, color: SYSTEM_COLORS.GRAY.DIM_GRAY, background: SYSTEM_COLORS.WHITE.ALABASTER, borderRadius: 20, padding: "2px 10px" }}>
            {filtered.length} de {activeOppsWithTypes.length}
          </p>
        </hgroup>
      </div>

      <div 
        style={{ 
          display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", 
          background: SYSTEM_COLORS.GRAY.CULTURED, border: `1px solid ${SYSTEM_COLORS?.WHITE?.BONE}`, 
          borderRadius: 14, padding: 10 }}>

        <ProductHierarchyFilter
          ctx={{ detailsByLevel, hierarchy, selectedPaths: hierarchyFilter }}
          uiState={{ label: "Produto" }}
          actions={{ onChange: setHierarchyFilter }}
        />

        <FilterDropdown
          ctx={{ options: typeOptions, selectedKeys: typeFilter }}
          uiState={{
            label: "Tipo de Oportunidade",
            icon: (
              <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke={SYSTEM_COLORS.GRAY.TAUPE_GRAY} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m12.83 2.18-9.24 3.9a1 1 0 0 0 0 1.84l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.84l-8.58-3.9a2 2 0 0 0-1.66 0Z" />
                <path d="M2 12a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 12" />
                <path d="M2 17a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 17" />
              </svg>
            ),
            variant: "radio",
          }}
          actions={{ onChange: setTypeFilter }}
        />
        <FilterDropdown
          actions={{ onChange: setTrendFilter }}
          ctx={{ options: TREND_OPTIONS, selectedKeys: trendFilter }}
          uiState={{
            label: "Alertas de Tendência",
            icon: (
              <svg  
                width={12} height={12} viewBox="0 0 24 24" fill="none" stroke={SYSTEM_COLORS.GRAY.TAUPE_GRAY} 
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
              </svg>
            ),
            variant: "radio",
          }}/>

        {(typeFilter.length + trendFilter.length + hierarchyFilter.length) > 0 && (
          <button
            onClick={clearFilters}
            className="oi-filter-btn-fill"
            style={{
              display: "flex", alignItems: "center", gap: 6,
              border: `1px solid ${SYSTEM_COLORS.GRAY.LIGHT_GRAY}`, borderRadius: 12, background: SYSTEM_COLORS?.WHITE?.WHITE,
              boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
              color: SYSTEM_COLORS?.GRAY?.CHARCOAL, fontSize: 12.5, fontWeight: 600, cursor: "pointer",
              padding: "8px 14px", height: 36,
            }}>

            <svg 
              width={13} height={13} viewBox="0 0 24 24" fill="none" stroke={SYSTEM_COLORS.GRAY.TAUPE_GRAY} 
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
              <path d="M3 3v5h5" />
            </svg>
            Limpar filtros
          </button>
        )}

        <div style={{ 
          marginLeft: "auto", display: "flex", alignItems: "center", gap: 8, 
          fontSize: "0.75rem", fontWeight: 700, color: SYSTEM_COLORS?.GRAY?.DAVY_GRAY }}>
          {"ORDENAR POR"}
          <SortSelectNew
            ctx={{
              value: sort,
              options: [
                { key: "value", label: "Maior retorno" },
                { key: "name_asc", label: "A a Z" },
              ],
            }}
            actions={{ onChange: setSort }}
          />
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "3fr 1fr 1fr 1fr 1fr",
          alignItems: "center",
          background: SYSTEM_COLORS?.GRAY?.CULTURED,
          border: `1px solid ${SYSTEM_COLORS?.WHITE?.BONE}`,
          borderRadius: 10,
          padding: "8px 12px",
          fontSize: "0.688rem",
          fontWeight: 700,
          color: SYSTEM_COLORS.GRAY.SONIC_SILVER,
          letterSpacing: 0.4,
          fontFamily: "Arial, sans-serif",
        }}> 
        <p style={{ fontSize: "0.75rem", textAlign: "left", fontWeight: 500 }}>
          PRODUTO
        </p>

        <p style={{ fontSize: "0.75rem", textAlign: "center", fontWeight: 500 }}>
          OPORTUNIDADE
        </p>

        <p style={{ fontSize: "0.75rem", textAlign: "center", fontWeight: 500 }}>
          RETORNO ESPERADO
        </p>

        <p style={{ fontSize: "0.75rem", textAlign: "center", fontWeight: 500 }}>
          TENDÊNCIA
        </p>

        <p style={{ fontSize: "0.75rem", textAlign: "center", fontWeight: 500 }}>
          AÇÕES
        </p>
      </div>


      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {filtered.length === 0 ? (
          <div style={{
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            gap: 8, padding: "40px 20px", color: SYSTEM_COLORS.DIM_GRAY, fontSize: 13,
          }}>
            Nenhuma oportunidade encontrada com os filtros selecionados.
          </div>
        ) : (
          filtered.map((opportunity) => (
            <div
              key={opportunity.opportunityId}
              ref={(el) => { rowRefs.current[opportunity.opportunityId] = el; }}
              className="oi-card-hover"
              style={{
                border: highlightedId === opportunity.opportunityId ? `2px solid ${SYSTEM_COLORS.RED.CORAL_RED}` : "1px solid transparent",
                borderRadius: 30, transition: "border-color 0.3s ease", backgroundColor: SYSTEM_COLORS.WHITE.WHITE_SMOKE }}>
              <ListItem
                actions={{
                  handleRemoveOpportunity,
                  resetModalState,
                  setSelectedId,
                  setRemovalReason,
                  resetOppError
                }}
                ctx={{
                  opportunity,
                  activeOppsWithTypes,
                  options,
                  removalReason,
                  selectedId,
                  products,
                }}
                uiState={{
                  reactivateOpportunity: true,
                  accordionButton: true,
                  page: 'detailed_opportunities',
                  delimiter: " · ",
                  updateOppError,
                  CardSection: OpportunityMetricsPanel,
                  cardSectionCtx: { 
                    opportunityTrendsDetails: opportunityTrendsDetails,
                   },
                  cardSectionUiState: {
                    isLoadingTrendsDetails: isLoadingTrendsDetails,
                  },
                }}
              />
            </div>
          ))
        )}
      </div>
    </section>
  );
};